import { isNonEmpty } from '@sgme/fp';
import { CompetitionParameterTableRow } from 'components/missed-deal-feedback/ready/table/row';
import { useAppSelector } from 'hooks/useAppSelector';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { FormIdProps } from 'shared/props';
import {
  missedDealCompetitionParameterFeedbackChanged,
  missedDealCompetitionParameterFeedbackCleared,
  missedDealCompetitionParameterFeedbackRemoved,
} from 'state/missed-deal-feeedback/actions';
import { CompetitionParameterFeedback } from 'state/missed-deal-feeedback/competition-parameters-feedback/model';
import { MissedFeedbackCompetitionParameter } from 'state/missed-deal-feeedback/feedback/model';
import {
  getCompetitionParametersFeedbacks,
  getCompetitionParametersReferential,
} from 'state/missed-deal-feeedback/selectors';

const UPFRONT_LABEL = 'upfront';

function canAddFeedbackParameter(
  availableParameters: readonly string[],
  feedbacks: readonly CompetitionParameterFeedback[],
) {
  const hasAvailableParameters = isNonEmpty(availableParameters);
  const hasUpfrontParameter = feedbacks.some(feedback => feedback.label === UPFRONT_LABEL);
  const isUpfrontOnlyParameterRemaining =
    availableParameters.length === 1 && feedbacks.length >= 1 && availableParameters.includes(UPFRONT_LABEL);
  const lastAvailableIsNotAffected =
    availableParameters.length === 1 && feedbacks.some(feedback => feedback.label === undefined);
  return (
    hasAvailableParameters && !hasUpfrontParameter && !isUpfrontOnlyParameterRemaining && !lastAvailableIsNotAffected
  );
}
function getAvailableFeedbackParameters(
  allCompetitionParameters: MissedFeedbackCompetitionParameter[],
  feedbacks: readonly CompetitionParameterFeedback[],
): readonly string[] {
  const availableFeedbackParameters: string[] = [];
  const hasAtMostOneFeedback = feedbacks.length <= 1;
  for (const { label } of allCompetitionParameters) {
    if (
      label !== undefined &&
      (hasAtMostOneFeedback || label !== UPFRONT_LABEL) &&
      !feedbacks.some(feedback => feedback.label === label)
    ) {
      availableFeedbackParameters.push(label);
    }
  }
  return availableFeedbackParameters;
}

export function CompetitionParametersTable({ formId }: FormIdProps) {
  const allCompetitionParameters = useAppSelector(s => getCompetitionParametersReferential(s, formId) ?? []);
  const feedbacks = useAppSelector(s => getCompetitionParametersFeedbacks(s, formId) ?? []);

  const availableParameters = getAvailableFeedbackParameters(allCompetitionParameters, feedbacks);

  const dispatch = useDispatch();
  const onChange = (index: number, feedback: MissedFeedbackCompetitionParameter) =>
    dispatch(missedDealCompetitionParameterFeedbackChanged(formId, index, feedback));
  const addNewFeedback = () => {
    const label = availableParameters.length === 1 ? availableParameters[0] : undefined;

    const newFeedback: MissedFeedbackCompetitionParameter = { label };
    const endIndex = feedbacks.length;
    onChange(endIndex, newFeedback);
  };

  const addButtonDisabled = feedbacks.some(f => f.bestOfferValue === undefined);
  const addButtonVisible = canAddFeedbackParameter(availableParameters, feedbacks);
  const canClearAll = feedbacks.length !== 0;

  return (
    <>
      <table
        className="table table-sm border table-form mt-4 mb-0 ms-xxl-auto w-100"
        data-e2e="competition-parameters-table"
      >
        <thead>
          <tr>
            <th className="w-50">
              <div className="align-items-center d-flex flex-nowrap justify-content-between">
                <FormattedMessage id="missedDealFeedback.header.competitionParameter" />
              </div>
            </th>
            <th className="w-25">
              <div className="align-items-center d-flex flex-nowrap justify-content-between">
                <FormattedMessage id="missedDealFeedback.header.sg" />
              </div>
            </th>
            <th className="w-25">
              <div className="align-items-center d-flex flex-nowrap justify-content-between">
                <FormattedMessage id="missedDealFeedback.header.bestOffer" />
              </div>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {feedbacks.map((feedback, index) => {
            const selectedParameter = allCompetitionParameters.find(parameter => parameter.label === feedback.label);
            return (
              <CompetitionParameterTableRow
                key={`competitionParameter${index}`}
                selectedParameter={selectedParameter}
                availableParameters={availableParameters}
                bestOfferValue={feedback.bestOfferValue}
                bestOfferFrequency={feedback.bestOfferFrequency}
                onChange={feedback => onChange(index, feedback)}
                onRemove={() => dispatch(missedDealCompetitionParameterFeedbackRemoved(formId, index))}
              />
            );
          })}
        </tbody>
      </table>

      <div className="text-end mt-3" role="group">
        {addButtonVisible && (
          <button
            className="btn btn-flat-primary btn-icon-start"
            data-e2e="missedDealFeedback-add-parameters"
            onClick={addNewFeedback}
            disabled={addButtonDisabled}
          >
            <i className="icon">add</i>
            <FormattedMessage id="missedDealFeedback.addCompetitionParameter" />
          </button>
        )}
        <button
          className="btn btn-flat-secondary btn-icon-start"
          data-e2e="missedDealFeedback-clear-parameters"
          onClick={() => dispatch(missedDealCompetitionParameterFeedbackCleared(formId))}
          disabled={!canClearAll}
        >
          <FormattedMessage id="clearAll" />
          <i className="icon ps-3">replay</i>
        </button>
      </div>
    </>
  );
}
